import { PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react';

import { Box, BoxProps, List, ListIcon, ListItem, Skeleton, Text, useToken } from '@chakra-ui/react';
import { CheckIcon } from '@udacity/chakra-uds-icons';
import { headerHeight } from '@udacity/udacity-header-v2';

import { Card } from '~/components/card';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { trackEvent } from '~/features/analytics/track-event';
import { BillingFrequency, billingFrequencySchema } from '~/features/checkout/models/checkout';
import { MobileStickyBanner } from '~/features/experiments/ab_plp_redesign_2025/components/mobile-sticky-banner';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';
import { useStandaloneProgramPrice } from '~/features/plans/hooks/use-standalone-program-price';
import { Program } from '~/features/program/models/program';
import { usePromotion } from '~/features/promotions/hooks/use-promotion';
import { useI18n } from '~/hooks/use-i18n';

import { CountdownCompactContainer } from './countdown-compact';
import { PlanCard, PlanGroup, PricePlanRadioGroup } from './plan-radio';

const STANDALONE_FEATURES = [
  'Flexibility to finish the program in your own time',
  'Hands-on projects with expert feedback',
  'Personalized project reviews',
  'Program Certificate',
];

const ALL_ACCESS_MONTHLY_FEATURES = [
  'Hands-on projects with expert feedback',
  'Personalized career coaching and interview prep',
  'Program Certificates',
];

const ALL_ACCESS_BUNDLE_FEATURES = [
  'Save 15% more compared to monthly',
  'Hands-on projects with expert feedback',
  'Personalized career coaching and interview prep',
  'Program Certificates',
];

export type PriceCardProps = StaticPriceCardProps & {
  renderButton?: (selectedPlan: BillingFrequency, buttonLabel: string) => ReactNode;
  isMobile?: boolean;
  defaultSelectedPlan?: BillingFrequency;
} & BoxProps;

export function PriceCard({
  programKey,
  programTitle,
  programSemanticType,
  renderButton,
  isMobile = false,
  defaultSelectedPlan,
  ...cardProps
}: PriceCardProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [bottomGutterHeight, setBottomGutterHeight] = useState<number>(0);
  const [selectedPlan, setSelectedPlan] = useState<BillingFrequency>(
    defaultSelectedPlan || billingFrequencySchema.enum.monthly
  );
  const [showMobileBanner, setShowMobileBanner] = useState<boolean>(false);
  const { currency } = useCurrency();
  const { data: plans } = usePricePlans(currency);
  const { data: standalonePlan, isFetched: standalonePlanIsFetched } = useStandaloneProgramPrice({
    nodeKey: programKey,
  });
  const { data: promoData } = usePromotion();
  const buttonLabel = promoData?.countdownConfig?.buttonLabelOverride ?? 'Enroll Now';

  useEffect(() => {
    const node = containerRef.current;
    if (!isMobile || !node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setShowMobileBanner(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        }
      },
      {
        threshold: 0,
      }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile]);

  const monthlyPlan = plans?.prices[billingFrequencySchema.enum.monthly]?.individual;
  const fourMonthPlan = plans?.prices[billingFrequencySchema.enum['4month']]?.individual;

  const ALL_ACCESS_PLAN_ITEMS = [
    {
      label: '4-Months Prepay',
      value: billingFrequencySchema.enum['4month'],

      ...fourMonthPlan,
      displayPrice: fourMonthPlan?.displayPricePerMonth,
      displayPriceUnits: ' /month',
      strikeThroughDisplayPrice: fourMonthPlan?.strikeThroughDisplayPricePerMonth,
      savingsBlurb: 'Save 15%',
    },
    {
      label: 'Monthly Payment',
      value: billingFrequencySchema.enum.monthly,
      ...monthlyPlan,
      displayPrice: monthlyPlan?.displayPricePerMonth,
      displayPriceUnits: ' /month',
      savingsBlurb: 'Flexible',
    },
  ];

  const STANDALONE_PLAN_ITEM = {
    label: 'Single Payment',
    value: billingFrequencySchema.enum.purchase,
    ...standalonePlan,
    displayPriceUnits: ' /one-time',
    savingsBlurb: undefined,
  };

  function getAvailablePlans() {
    if (programSemanticType === 'Course') return undefined;
    // Note: this is a way to prevent layout shifts when standalone plan is fetching.
    // Since Nanodegree programs _should_ have a standalone plan, we will optimistically show the standalone plan with a skeleton over the price.
    if (!standalonePlanIsFetched)
      return {
        subscription: ALL_ACCESS_PLAN_ITEMS,
        standalone: programSemanticType === 'Degree' ? STANDALONE_PLAN_ITEM : undefined,
      };

    if (standalonePlanIsFetched && !standalonePlan)
      return {
        subscription: ALL_ACCESS_PLAN_ITEMS,
        standalone: undefined,
      };

    return {
      subscription: ALL_ACCESS_PLAN_ITEMS,
      standalone: STANDALONE_PLAN_ITEM,
    };
  }

  function handlePlanChange(plan: BillingFrequency) {
    let label = `${plan}_pricecard_clicked`;
    if (plan === billingFrequencySchema.enum.purchase) {
      label = 'onetime_purchase_pricecard_clicked';
    }
    trackEvent({
      name: 'Product Page CTA Clicked',
      type: 'click',
      label,
      additionalAttributes: {
        product_ids: [programKey],
      },
    });

    setSelectedPlan(plan);
  }

  const availablePlans = getAvailablePlans();

  return (
    <>
      <Card
        ref={containerRef}
        bgColor='blue.100'
        borderRadius='12px'
        borderWidth='0px'
        data-testid={isMobile ? 'mobile-plans' : 'desktop-plans'}
        display='flex'
        flexDirection='column'
        gap={4}
        scrollMarginTop={`calc(${headerHeight} + 24px)`}
        sx={{
          '--bottom-gutter-height': `calc(${bottomGutterHeight}px + 24px)`,
        }}
        {...cardProps}
      >
        <PricePlanRadioGroup value={selectedPlan} onChange={handlePlanChange}>
          {({ getRadioProps }) => (
            <>
              {availablePlans?.subscription && (
                <PlanGroup
                  title='Udacity Subscription'
                  description={
                    <List display='flex' flexDirection='column' gap={1} mb={3}>
                      {(selectedPlan === billingFrequencySchema.enum.monthly
                        ? ALL_ACCESS_MONTHLY_FEATURES
                        : ALL_ACCESS_BUNDLE_FEATURES
                      ).map((feature) => (
                        <ListItem key={feature} display='flex'>
                          <ListIcon alignSelf='flex-start' as={CheckIcon} color='blue.500' height='20px' width='20px' />
                          <HeadingWithEmphasis
                            text={feature}
                            emphasisProps={{
                              fontWeight: '600',
                            }}
                            headingProps={{
                              as: 'span',
                              fontSize: '0.8125rem',
                              lineHeight: '1.4',
                              fontFamily: 'body',
                              fontWeight: '400',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  }
                  isActive={
                    selectedPlan === billingFrequencySchema.enum['4month'] ||
                    selectedPlan === billingFrequencySchema.enum.monthly
                  }
                  subtitle={
                    <Text color='blue.500' fontSize='0.8125rem' fontWeight='500' lineHeight='1.4'>
                      Access to this course and 500+ more
                    </Text>
                  }
                  onDetailsOpen={() => {
                    // Note: there is a requirement to reset the selected plan if the details are opened while the standalone plan is selected.
                    if (selectedPlan === billingFrequencySchema.enum['purchase']) {
                      setSelectedPlan(defaultSelectedPlan || billingFrequencySchema.enum.monthly);
                    }

                    trackEvent({
                      name: 'Product Page CTA Clicked',
                      type: 'click',
                      label: 'expand_pricecard_info',
                      value: selectedPlan,
                      additionalAttributes: {
                        product_ids: [programKey],
                      },
                    });
                  }}
                >
                  <PlanCard
                    couponApplied={fourMonthPlan?.couponApplied}
                    couponPercentOff={fourMonthPlan?.couponPercentOff}
                    item={ALL_ACCESS_PLAN_ITEMS[0]}
                    {...getRadioProps({ value: ALL_ACCESS_PLAN_ITEMS[0].value })}
                  />
                  <PlanCard
                    couponApplied={monthlyPlan?.couponApplied}
                    couponPercentOff={monthlyPlan?.couponPercentOff}
                    item={ALL_ACCESS_PLAN_ITEMS[1]}
                    {...getRadioProps({ value: ALL_ACCESS_PLAN_ITEMS[1].value })}
                  />
                </PlanGroup>
              )}
              {availablePlans?.standalone && (
                <PlanGroup
                  isActive={selectedPlan === billingFrequencySchema.enum.purchase}
                  title='Single Program'
                  description={
                    <List display='flex' flexDirection='column' gap={1} mb={3}>
                      {STANDALONE_FEATURES.map((feature) => (
                        <ListItem key={feature} display='flex'>
                          <ListIcon alignSelf='flex-start' as={CheckIcon} color='blue.500' height='20px' width='20px' />
                          <HeadingWithEmphasis
                            text={feature}
                            emphasisProps={{
                              fontWeight: '600',
                            }}
                            headingProps={{
                              as: 'span',
                              fontSize: '0.8125rem',
                              lineHeight: '1.4',
                              fontFamily: 'body',
                              fontWeight: '400',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  }
                  subtitle={
                    <Text color='grey.500' fontSize='0.8125rem' fontWeight='500' lineHeight='1.4'>
                      Get instant access to this course
                    </Text>
                  }
                  onDetailsOpen={() => {
                    // Note: there is a requirement to reset the selected plan if the details are opened while the subscription plan is selected.
                    if (
                      selectedPlan === billingFrequencySchema.enum['4month'] ||
                      selectedPlan === billingFrequencySchema.enum.monthly
                    ) {
                      setSelectedPlan(billingFrequencySchema.enum.purchase);
                    }

                    trackEvent({
                      name: 'Product Page CTA Clicked',
                      type: 'click',
                      label: 'expand_pricecard_info',
                      value: 'onetime_purchase',
                      additionalAttributes: {
                        product_ids: [programKey],
                      },
                    });
                  }}
                >
                  <PlanCard
                    couponApplied={standalonePlan?.couponApplied}
                    couponPercentOff={standalonePlan?.couponPercentOff}
                    item={availablePlans.standalone}
                    {...getRadioProps({ value: availablePlans.standalone.value })}
                  />
                </PlanGroup>
              )}
              <CountdownCompactContainer
                renderButton={(plan, buttonLabel) => renderButton?.(plan, buttonLabel)}
                selectedPlan={selectedPlan}
              />
            </>
          )}
        </PricePlanRadioGroup>
        <DynamicPositionedBox onResize={setBottomGutterHeight}>
          <List as='ol' color='gray.700' display='flex' flexDirection='column' gap={1}>
            {[
              'Discount applies to the first 4 months of membership, after which plans are converted to month-to-month.',
              <>
                Total price of {fourMonthPlan?.displayPrice ?? <Skeleton as='span' height='12.5px' width='25px' />} for
                a 4-month bundle, based on the average time to complete a Nanodegree program.
              </>,
            ].map((content, index) => (
              <ListItem key={index}>
                <Text fontSize='2xs'>
                  <Box as='sup'>{index + 1}</Box> {content}
                </Text>
              </ListItem>
            ))}
          </List>
        </DynamicPositionedBox>
      </Card>

      {isMobile && (
        <MobileStickyBanner buttonSlot={renderButton?.(selectedPlan, buttonLabel)} isVisible={showMobileBanner} />
      )}
    </>
  );
}

function DynamicPositionedBox({ children, onResize }: PropsWithChildren<{ onResize?: (newHeight: number) => void }>) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<number | undefined>(undefined);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.target.getBoundingClientRect().height;
        setPosition(height);
        onResize?.(height);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [onResize]);
  return (
    <Box
      ref={ref}
      bottom={`calc(-${position}px - 24px)`}
      opacity={typeof position === 'number' ? 1 : 0}
      position='absolute'
    >
      {children}
    </Box>
  );
}

export function transformStaticPriceCardProps({ program }: { program: Program }) {
  return {
    programKey: program.key,
    programSemanticType: program.semanticType,
    programTitle: program.title,
  };
}

export type StaticPriceCardProps = ReturnType<typeof transformStaticPriceCardProps>;

export type StickyPriceCardTrackWrapperProps = {
  children: ReactNode;
  priceCardSlot: ReactNode;
  imageSlot: ReactNode;
};

export function StickyPriceCardTrackWrapper({ children, priceCardSlot, imageSlot }: StickyPriceCardTrackWrapperProps) {
  const [contentMaxWidth] = useToken('sizes', ['content-max-width']);
  const TRACK_WIDTH_PIXELS = '404px';
  const { isRtl } = useI18n();
  return (
    <Box
      position='relative'
      sx={{
        '--track-padding-end': TRACK_WIDTH_PIXELS,
        '--carousel-container-padding-end': TRACK_WIDTH_PIXELS,
      }}
    >
      {children}
      <Box
        bottom={0}
        maxWidth={TRACK_WIDTH_PIXELS}
        paddingBottom={6}
        position='absolute'
        top='0px'
        width='100%'
        display={{
          base: 'none',
          lg: 'block',
        }}
        {...(isRtl
          ? {
              left: {
                base: 0,
                xl: `calc((100% - ${contentMaxWidth})/2)`,
              },
            }
          : {
              right: {
                base: 0,
                xl: `calc((100% - ${contentMaxWidth})/2)`,
              },
            })}
      >
        <Box
          bottom={0}
          maxWidth={TRACK_WIDTH_PIXELS}
          paddingBottom={12}
          position='absolute'
          top='32px'
          width='100%'
          display={{
            base: 'none',
            lg: 'block',
          }}
        >
          {imageSlot}
          {priceCardSlot}
        </Box>
      </Box>
    </Box>
  );
}
